import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import TextField from '@material-ui/core/TextField';

import { loadEndEmploymentReasons } from './actions/load-end-employment-reasons';
import { updateEmployee } from './actions/update-employee';

import { UiButton } from '../ui/ui-button';
import { UiFilterSelect } from '../ui/ui-filter-select';

import { JobTitleAutocomplete } from './job-title-autocomplete';
import { RehireEligibleFormBody } from './rehire-eligible-form-body';

import { REHIRE_ELIGIBLE_TYPES } from './utils/rehire-eligible-types';
import {
  END_EMPLOYMENT_REASON_TEXT,
  END_EMPLOYMENT_REASON_TYPES,
} from '../util/end-employment-reason-types';
import { isValueANumber } from '../util/number-utils';

const mapStateToProps = (state: any) => {
  const endReasons: Array<EndEmploymentReason> | null =
    state.employees.endEmploymentReasons || null;
  return {
    endReasons,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { employee }: InputProps,
) => ({
  actions: {
    loadEndEmploymentReasons: () => dispatch(loadEndEmploymentReasons()),
    updateEmployee: (update: Partial<EmployeeUpdateRequest>) =>
      dispatch(updateEmployee(employee.employeeId, update)),
  },
});

type InputProps = {
  employee: Employee;
  open: boolean;
  cancelCallback: () => void;
  saveCallback: () => void;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EndEmploymentDialog = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const { employee, open, cancelCallback, saveCallback, endReasons, actions } = props;

  const [selectedEndReasonCategory, setSelectedEndReasonCategory] = useState<'QUIT' | 'FIRED'>(
    END_EMPLOYMENT_REASON_TYPES.QUIT,
  );
  const [selectedEndReasonId, setSelectedEndReasonId] = useState<number | null>(null);
  const [endReasonExplanation, setEndReasonExplanation] = useState<string | null>(null);
  const [rehireEligible, setRehireEligible] = useState<RehireEligibleOptions | null>(
    employee.rehireEligible || null,
  );
  const [jobTitle, setJobTitle] = useState<string>(employee.jobTitle || '');
  const [jobId, setJobId] = useState<number | null>(employee.jobId || null);

  useEffect(() => {
    actions.loadEndEmploymentReasons();
  });

  const handleChangeEndReasonCategory = (updatedReasonCategory: string) => {
    if (
      updatedReasonCategory === END_EMPLOYMENT_REASON_TYPES.QUIT ||
      updatedReasonCategory === END_EMPLOYMENT_REASON_TYPES.FIRED
    ) {
      if (selectedEndReasonCategory !== updatedReasonCategory) {
        setSelectedEndReasonId(null);
      }
      setSelectedEndReasonCategory(updatedReasonCategory);
    }
  };

  const handleChangeEndReasonId = (e: React.ChangeEvent<HTMLInputElement>) => {
    const endReasonId = isValueANumber(e.target.value) ? parseInt(e.target.value, 10) : null;
    setSelectedEndReasonId(endReasonId);
  };

  const handleChangeReasonExplanation = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndReasonExplanation(e.target.value);
  };

  const handleChangeRehireEligible = (newRehireEligible: string | null) => {
    if (
      newRehireEligible === REHIRE_ELIGIBLE_TYPES.YES ||
      newRehireEligible === REHIRE_ELIGIBLE_TYPES.NO ||
      newRehireEligible === REHIRE_ELIGIBLE_TYPES.DONT_KNOW
    ) {
      setRehireEligible(newRehireEligible);
    }
  };

  const saveEndEmploymentReason = () => {
    actions.updateEmployee({
      endReasonId: selectedEndReasonId,
      endReasonExplanation: endReasonExplanation || null,
      isCurrentEmployee: false,
      rehireEligible,
      jobTitle: jobTitle || null,
      jobId,
    });

    saveCallback();
  };

  const quitFiredReasons = [
    {
      value: END_EMPLOYMENT_REASON_TYPES.QUIT,
      label: END_EMPLOYMENT_REASON_TEXT[END_EMPLOYMENT_REASON_TYPES.QUIT],
    },
    {
      value: END_EMPLOYMENT_REASON_TYPES.FIRED,
      label: END_EMPLOYMENT_REASON_TEXT[END_EMPLOYMENT_REASON_TYPES.FIRED],
    },
  ];

  const EndReasonSelect = () => (
    <div className="u-display-flex u-margin-bottom-lg">
      <UiFilterSelect
        label="Status"
        value={quitFiredReasons.find((r) => r.value === selectedEndReasonCategory)?.value || ''}
        options={quitFiredReasons}
        onChange={handleChangeEndReasonCategory}
        hideClear
      />
    </div>
  );

  const EndReasonOptions = ({
    endReasonOptions,
  }: {
    endReasonOptions: Array<EndEmploymentReason>;
  }) => (
    <div className="u-margin-x-md card-body">
      {endReasonOptions.map((r) => (
        <div key={`end-employment-reason-${r.endReasonId}`}>
          <FormControlLabel
            control={
              <Radio
                checked={selectedEndReasonId === r.endReasonId}
                onChange={handleChangeEndReasonId}
                value={r.endReasonId.toString()}
                key={`end-employment-reason-${r.endReasonId}`}
              />
            }
            label={r.endReasonDescription}
          />
        </div>
      ))}
    </div>
  );

  if (!endReasons) {
    return null;
  }

  const filteredEndReasons = endReasons.filter((r) => r.type === selectedEndReasonCategory);
  const selectedEndReason = endReasons.find((r) => r.endReasonId === selectedEndReasonId);

  const saveDisabled = selectedEndReasonId === null || rehireEligible === null;

  return (
    <Dialog open={open} onClose={cancelCallback} maxWidth="xs" fullWidth>
      <DialogTitle>Why is {employee.Contact.Person.firstName} leaving?</DialogTitle>
      <DialogContent>
        <EndReasonSelect />
        <EndReasonOptions endReasonOptions={filteredEndReasons} />
        <TextField
          className="u-width-100 u-margin-bottom-lg"
          id="endReasonExplanation"
          label="Please explain"
          value={endReasonExplanation || ''}
          onChange={handleChangeReasonExplanation}
          required={!!selectedEndReason?.requiresExplanation}
        />
        <RehireEligibleFormBody
          employee={employee}
          rehireEligible={rehireEligible}
          setRehireEligible={handleChangeRehireEligible}
        />
        {rehireEligible && rehireEligible !== REHIRE_ELIGIBLE_TYPES.NO ? (
          <JobTitleAutocomplete
            orgId={employee.orgId}
            jobTitle={jobTitle}
            setJobTitle={setJobTitle}
            jobId={jobId}
            setJobId={setJobId}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <UiButton onClick={cancelCallback} variant="dismiss">
          Cancel
        </UiButton>
        <UiButton disabled={saveDisabled} onClick={saveEndEmploymentReason}>
          Terminate
        </UiButton>
      </DialogActions>
    </Dialog>
  );
});
